import React, { useState } from "react";
import { RefButton } from "../../../like-button-ref/like-button-ref";
import { useFormik } from "formik";
import * as yup from "yup";
import ReactModal from "react-modal";
import classNames from "classnames";
import { formatPhoneNumber } from "../../../../helpers/formatPhoneNumber";
import { Translate } from "../../../../locales/locales-utils";
import { BrandingExtendedProps } from "./types";
import { RequestDataType } from "../../../../types/demoRequestTypes";
import { API_SERVER_URL } from "gatsby-env-variables-fork";
import { useBackendSettings } from "../../../../layouts/useBackendSettings";

export const BrandingExtendedForm = (props: BrandingExtendedProps) => {
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [selectedValue, setSelectedValue] = useState("light");

  const backendSettings = useBackendSettings();

  const validationSchema = (needConfirmUserAgreement: boolean, needConfirmPersonDataProcessing: boolean) => {
    return yup.object().shape({
      name: yup
        .string()
        .strict()
        .trim(
          Translate("Global.ContactUsForm.ItemNameTrimmedValidationMessage")
        )
        .min(2, Translate("Global.ContactUsForm.ItemNameMinValidationMessage"))
        .max(20, Translate("Global.ContactUsForm.ItemNameMaxValidationMessage"))
        .matches(
          /^[a-zA-Zа-яА-Я'][a-zA-Zа-яА-Я-' ]+[a-zA-Zа-яА-Я']?$/u,
          Translate("Global.ContactUsForm.ItemNameAlphabetValidationMessage")
        )
        .required(
          Translate("Global.ContactUsForm.ItemNameRequiredValidationMessage")
        ),
      email: yup
        .string()
        .email(
          Translate("Global.BrandingForm.ItemEmailInvalidValidationMessage")
        )
        .required(
          Translate("Global.BrandingForm.ItemEmailRequiredValidationMessage")
        ),
      phoneNumber: yup
        .string()
        .min(
          18,
          Translate("Global.BrandingForm.ItemPhoneInvalidValidationMessage")
        )
        .required(
          Translate("Global.BrandingForm.ItemPhoneInvalidValidationMessage")
        ),
      brandingAgreements: yup.bool().oneOf([true]),
      userAgreements: needConfirmUserAgreement
        ? yup.bool().oneOf([true])
        : yup.boolean(),
      personDataProcessing: needConfirmPersonDataProcessing
        ? yup.bool().oneOf([true])
        : yup.boolean(),
    });
  };

  const setBrandingNameByType = (type: string): string => {
    let result: string;

    switch (type) {
      case "standart":
        result = Translate("Global.BrandingForm.ItemBrandingOptions.Standart");
        break;
      case "event-mode":
        result = Translate("Global.BrandingForm.ItemBrandingOptions.EventMode");
        break;
      case "event-mode-plus-standart":
        result = Translate(
          "Global.BrandingForm.ItemBrandingOptions.EventModePlusStandart"
        );
        break;
      default:
        result = Translate("Global.BrandingForm.ItemBrandingOptions.Light");
    }

    return `Event type: ${result}`;
  };

  const form = useFormik({
    initialValues: {
      name: "",
      email: "",
      phoneNumber: "",
      brandingAgreements: false,
      message: "",
      userAgreements: false,
      personDataProcessing: false
    },
    validateOnBlur: false,
    validateOnChange: true,
    validationSchema: validationSchema(
      backendSettings?.needConfirmUserAgreement ?? false,
      backendSettings?.needConfirmPersonDataProcessing ?? false,
    ),
    onSubmit: async (values) => {
      const response = await fetch(`${API_SERVER_URL}/api/demo/add`, {
        credentials: "include",
        referrer: "/",
        method: "POST",
        cache: "no-cache",
        headers: { "Content-Type": "application/json; charset=UTF-8" },
        body: JSON.stringify({
          UseTo: "Landings",
          FirstName: values.name || "",
          Email: values.email || "",
          Phone: values.phoneNumber || "",
          Data: `${setBrandingNameByType(selectedValue)} Additional services: ${
            values.message ? values.message : "—"
          }`,
          AcceptedPrivacy: values.brandingAgreements || "",
          AcceptedUserAgreement: values.userAgreements || "",
          AcceptedPersonDataProcessing: values.personDataProcessing || "",
          Type: RequestDataType.Brands,
          RequestURL: window.location.href,
        }),
      });
      if (response.ok) setShowSuccessMessage(true);
    },
  });

  const closeModal = () => {
    props.closeHandle();
    setShowSuccessMessage(false);
    form.resetForm();
  };

  return (
    <ReactModal
      isOpen={props.isOpen}
      bodyOpenClassName="modalWhiteBg modalForm"
    >
      <button className={"modalCloseBtn"} onClick={closeModal}></button>
      <form
        onSubmit={form.handleSubmit}
        noValidate
        className={showSuccessMessage ? "formWasSend" : ""}
      >
        {showSuccessMessage && (
          <div className="formMessage Success">
            <div className="formMessage-Icon">
              <svg
                width="80"
                height="80"
                viewBox="0 0 80 80"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="40" cy="40" r="40" fill="#65C15D" />
                <path
                  d="M30 38.3334L37.4274 46.5861C37.4665 46.6296 37.5344 46.6304 37.5745 46.5879L51.6667 31.6667"
                  stroke="white"
                  strokeWidth="5"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="h2 formMessageTitle">
              {Translate("Global.BrandingForm.ItemSuccessTitle")}
            </div>
            <div className="h6 formMessageDescription">
              {Translate("Global.BrandingForm.ItemSuccessText")}
            </div>
            <div className="formMessageActions">
              <RefButton
                text={Translate("Global.BrandingForm.ItemSuccessButton")}
                variant="primary"
                size="lg"
                onClick={closeModal}
              />
            </div>
          </div>
        )}
        <div className="formHero">
          <div className="formHero-Title">
            {Translate("Global.BrandingForm.Title")}
          </div>
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
              form.errors.name && form.touched.name,
            },
            {
              ["formControlBrandingSuccess"]:
              !form.errors.name && form.values.name,
            }
          )}
        >
          <label htmlFor="YouName">
            {Translate("Global.ContactUsForm.ItemNameLabel")}
            <span className="required-star">*</span>
          </label>
          <input
            type="text"
            id="YouName"
            name="name"
            placeholder={Translate("Global.ContactUsForm.ItemNamePlaceholder")}
            value={form.values.name}
            onBlur={form.handleBlur}
            onFocus={() =>
              form.setTouched({
                name: true,
                email: false,
                phoneNumber: false,
                brandingAgreements: false,
                userAgreements: false,
              })
            }
            onChange={form.handleChange}
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value.replace(
                /[0-9|+|=|_|-|(|)|*|&|^|%|$|#|@|!|`|~|/.,|{|}|?|<|>|:|;|"|]/,
                ""
              );
            }}
          />
          {form.errors.name && form.touched.name && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.name}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
              form.errors.email && form.touched.email,
            },
            {
              ["formControlBrandingSuccess"]:
              !form.errors.email && form.values.email,
            }
          )}
        >
          <label htmlFor="YourEmail">
            {Translate("Global.BrandingForm.ItemEmailTitle")}
            <span className="required-star">*</span>
          </label>
          <input
            type="email"
            id="YourEmail"
            name="email"
            placeholder={Translate("Global.BrandingForm.ItemEmailPlaceholder")}
            value={form.values.email}
            onBlur={form.handleBlur}
            onChange={form.handleChange}
            onFocus={() =>
              form.setTouched({
                name: false,
                email: true,
                phoneNumber: false,
                brandingAgreements: false,
                userAgreements: false,
              })
            }
            onInput={(e) => {
              e.currentTarget.value = e.currentTarget.value
                .trim()
                .replace(
                  /[+|=|(|)|*|&|^|%|$|#|!|`|~|/,|{|}|?|<|>|:|;|'"|]/,
                  ""
                );
            }}
          />
          {form.errors.email && form.touched.email && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.email}
            </div>
          )}
        </div>
        <div
          className={classNames(
            "formControl",
            {
              ["formControlBrandingError"]:
              form.errors.phoneNumber && form.touched.phoneNumber,
            },
            {
              ["formControlBrandingSuccess"]:
              !form.errors.phoneNumber && form.values.phoneNumber,
            }
          )}
        >
          <label htmlFor="YourPhoneNumber">
            {Translate("Global.BrandingForm.ItemPhoneTitle")}
            <span className="required-star">*</span>
          </label>
          <input
            type="tel"
            id="YourPhoneNumber"
            name="phoneNumber"
            placeholder={Translate("Global.BrandingForm.ItemPhonePlaceholder")}
            value={form.values.phoneNumber}
            onBlur={form.handleBlur}
            maxLength={19}
            onFocus={() =>
              form.setTouched({
                name: false,
                email: false,
                phoneNumber: true,
                brandingAgreements: false,
                userAgreements: false,
              })
            }
            onChange={(e) => {
              form.setFieldValue(
                e.target.name,
                formatPhoneNumber(e.target.value, "+7 (000) 000-00-00")
              );
            }}
          />
          {form.errors.phoneNumber && form.touched.phoneNumber && (
            <div className="formControlBrandingErrorMessage">
              {form.errors.phoneNumber}
            </div>
          )}
        </div>
        <div className="formControl">
          <label htmlFor="YourBranding">
            {Translate("Global.BrandingForm.ItemBrandingTitle")}
          </label>
          <select
            value={selectedValue}
            onChange={(e) => setSelectedValue(e.target.value)}
          >
            <option value="light">
              {Translate("Global.BrandingForm.ItemBrandingOptions.Basic")}
            </option>
            <option value="standart">
              {Translate("Global.BrandingForm.ItemBrandingOptions.Standart")}
            </option>
            <option value="event-mode">
              {Translate("Global.BrandingForm.ItemBrandingOptions.EventMode")}
            </option>
            <option value="event-mode-plus-standart">
              {Translate(
                "Global.BrandingForm.ItemBrandingOptions.EventModePlusStandart"
              )}
            </option>
          </select>
        </div>
        <div className="formControl">
          <label htmlFor="Message">
            {Translate("Global.ContactUsForm.ItemAdditionalServicesTitle")}
          </label>
          <div className="formTextAreaContainer">
            <input
              type="text"
              id="Message"
              placeholder={Translate(
                "Global.ContactUsForm.ItemAdditionalServicesPlaceholder"
              )}
              name="Message"
              onChange={(e) => {
                form.handleChange("message")(e);
              }}
              value={form.values.message}
            />
          </div>
        </div>
        <div className="formAgreements">
          <div
            className={classNames("formAgreementsContainer", {
              ["error"]:
              form.errors.brandingAgreements &&
              form.touched.brandingAgreements,
            })}
          >
            <input
              checked={form.values.brandingAgreements}
              onChange={(event) => {
                const { checked } = event.target;
                form.setFieldValue("brandingAgreements", checked);
                form.setTouched({
                  name: false,
                  email: false,
                  phoneNumber: false,
                  brandingAgreements: true,
                  userAgreements: false,
                });
              }}
              type="checkbox"
              name="brandingAgreements"
              id="brandingAgreements"
            />
            <label htmlFor="brandingAgreements">
              {backendSettings?.needConfirmUserAgreement
                ? Translate(
                  "Global.BrandingForm.ItemBrandingAgreementsPrivacyPolicy"
                )
                : Translate("Global.BrandingForm.ItemBrandingAgreements")}
              <span className="required-star">*</span>
            </label>
          </div>
          {backendSettings?.needConfirmUserAgreement && (
            <div
              className={classNames("formAgreementsContainer", {
                ["error"]:
                form.errors.userAgreements && form.touched.userAgreements,
              })}
            >
              <input
                checked={form.values.userAgreements}
                onChange={(event) => {
                  const { checked } = event.target;
                  form.setFieldValue("userAgreements", checked);
                  form.setTouched({
                    name: false,
                    email: false,
                    phoneNumber: false,
                    brandingAgreements: false,
                    userAgreements: true,
                  });
                }}
                type="checkbox"
                name="userAgreements"
                id="userAgreements"
              />
              <label htmlFor="userAgreements">
                {Translate(
                  "Global.BrandingForm.ItemBrandingAgreementsUserAgreements"
                )}
                <span className="required-star">*</span>
              </label>
            </div>
          )}
          {backendSettings?.needConfirmPersonDataProcessing && (
            <div
                className={classNames("formAgreementsContainer", {
                    ["error"]:
                    form.errors.personDataProcessing && form.touched.personDataProcessing,
                })}
            >
                <input
                checked={form.values.personDataProcessing}
                onChange={(event) => {
                    const { checked } = event.target;
                    form.setFieldValue("personDataProcessing", checked);
                }}
                type="checkbox"
                name="personDataProcessing"
                id="personDataProcessing"
                />
                <label htmlFor="personDataProcessing">
                {Translate(
                    "Global.ContactUsExtendedForm.ItemAgreementsPersonDataProcessing"
                )}
                <span className="required-star">*</span>
                </label>
            </div>
            )}
        </div>
        <div className="formActionsBranding">
          <RefButton
            text={Translate("Global.BrandingForm.CancelButton")}
            variant="secondary"
            size="md"
            tag="button"
            onClick={closeModal}
          />
          <RefButton
            text={Translate("Global.BrandingForm.SubmitButton")}
            variant="primary"
            size="md"
            tag="button"
            type="submit"
          />
        </div>
      </form>
    </ReactModal>
  );
};
